import { type LocationQuery } from 'vue-router';
import { type BusPackageLoaderConfig } from './types';
import { type BusPathData } from '@search/trip/bus/types';
import { type BusLocation, type BusUrlParams } from '@utils/bus';
import type Bus from '@itinerary/trip/bus/Bus';
import type BusPriceClass from '@itinerary/trip/bus/BusPriceClass';
import { TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import BusPackage from '@itinerary/trip/bus/BusPackage';
import { TimeFilter, TripSortBy } from '@itinerary/trip/TripModuleTypes';
import {
    ButtonGroupFilter,
    CheckboxFilter,
    type Filter,
    LoadingStatus,
    PriceFilter,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import {
    BusFilters,
    type BusFiltersData,
} from '@itinerary/trip/bus/BusModuleTypes';
import TripModule from '@itinerary/trip/TripModule';
import { AutoReserveType, ReserveType } from '@utils/itinerary';
import BusPackageSet from '@itinerary/trip/bus/BusPackageSet';
import TaxiPackageSet from '@itinerary/trip/bus/TaxiPackageSet';

export default class BusModule extends TripModule<
    Bus,
    BusPriceClass,
    BusFiltersData,
    BusLocation,
    BusPackage,
    BusPackageSet | TaxiPackageSet
> {
    constructor(
        public descriptionTemplate = 'بلیط اتوبوس (مبدا) (مقصد), به صرفه‌ترین روش برای رزرو آنلاین بلیط اتوبوس (مبدا) به مقصد (مقصد) سریع، ارزان، راحت و ایمن با سامانه‌ی هوشمند وب سایت مِستربلیط، قیمت‌های ارزان و پشتیبانی کامل را به شما تضمین می‌دهیم.',
        public reserveType: ReserveType.Bus | ReserveType.Taxi = ReserveType.Bus
    ) {
        super();
    }

    override get defaultAutoReserveType() {
        return this.reserveType === ReserveType.Bus
            ? AutoReserveType.Route
            : AutoReserveType.None;
    }

    override getPackageSet(
        loaderConfig: BusPackageLoaderConfig
    ): BusPackageSet | TaxiPackageSet {
        const PackageSetClass =
            this.reserveType === ReserveType.Bus
                ? BusPackageSet
                : TaxiPackageSet;
        return new PackageSetClass(loaderConfig, this.getInitialFilters());
    }

    get searchForm() {
        switch (this.reserveType) {
            case ReserveType.Bus:
                return useBusForm();
            case ReserveType.Taxi:
                return useTaxiForm();
            default:
                throw new Error('Unsupported reserve type');
        }
    }

    override get reserveInfo() {
        return this.searchForm.reserveInfo;
    }

    override get emptyPackage() {
        const form = this.searchForm;
        return new BusPackage(
            {
                id: 42562610,
                from: Number(form.originCode),
                to: Number(form.destinationCode),
                fromName: this.originName,
                toName: this.destinationName,
                fromCity: this.originName,
                toCity: this.destinationName,
                departureTime: form.departureDate?.toISOString(),
                capacity: 0,
                superCorporation: 'مِستربلیط',
            },
            form.adultCount,
            false,
            this.reserveType
        );
    }

    override get showChangeDateSuggestion(): boolean {
        return true;
    }

    override get extraAutoReserveItem() {
        if (
            this.autoReservableList?.[0] &&
            this.sortedReservableList.length >= 4
        ) {
            return this.autoReservableList?.[0];
        }
        return null;
    }

    override get defaultSortBy(): TripSortBy {
        return this.reserveType === ReserveType.Taxi
            ? TripSortBy.MrbilitOffer
            : TripSortBy.Time;
    }

    override get sortByList(): SortBy[] {
        return [
            ...(this.reserveType === ReserveType.Taxi
                ? [
                      {
                          type: TripSortBy.MrbilitOffer,
                          label: 'پیشنهاد مِستربلیط',
                      },
                  ]
                : []),
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
        ];
    }

    override applySort(list: BusPackage[]): BusPackage[] {
        if (this.sortBy === TripSortBy.MrbilitOffer) {
            return list;
        } else {
            return super.applySort(list) as BusPackage[];
        }
    }

    override async terminatePackageReserve(_reserveId: number): Promise<void> {
        // this has problem with webapp, where the application is not restarted on after-order,
        // and may have errors somewhere else, where we hasn't catch yet.
        // so we decided to disable it temporarily
        // await CancelSeatReserveApiApiCancelSeatReserveGet({
        //     reserveId,
        // });
    }

    override get lateralLoader(): TaxiLateralLoader | null {
        return this.reserveType === ReserveType.Bus
            ? new TaxiLateralLoader()
            : null;
    }

    override applyFilter(
        pkg: BusPackage,
        filters: { [key in BusFiltersData['key']]: any }
    ): boolean {
        if (!pkg.segments.length) {
            return false;
        }

        const [bus] = pkg.segments[0].trips;
        if (!bus) {
            return false;
        }

        const departureMinutes = timeToMinutes(bus.departureTime);
        const arrivalMinutes =
            bus.arrivalTime && timeToMinutes(bus.arrivalTime);
        return (
            (!filters.carrier ||
                filters.carrier.length === 0 ||
                filters.carrier.includes(
                    bus.corporationInfo.code.toString()
                )) &&
            (!filters.price ||
                (pkg.singlePayable >= filters.price[0] &&
                    pkg.singlePayable <= filters.price[1])) &&
            (!filters.time ||
                !filters.time.departure ||
                filters.time.departure.length === 0 ||
                (departureMinutes <= 8 * 60 &&
                    filters.time.departure.includes('midnight')) ||
                (departureMinutes >= 8 * 60 &&
                    departureMinutes <= 18 * 60 &&
                    filters.time.departure.includes('day')) ||
                (departureMinutes >= 18 * 60 &&
                    filters.time.departure.includes('night'))) &&
            (!filters.time ||
                !filters.time.arrival ||
                filters.time.arrival.length === 0 ||
                (+arrivalMinutes <= 8 * 60 &&
                    filters.time.arrival.includes('midnight')) ||
                (+arrivalMinutes >= 8 * 60 &&
                    +arrivalMinutes <= 18 * 60 &&
                    filters.time.arrival.includes('day')) ||
                (+arrivalMinutes >= 18 * 60 &&
                    filters.time.arrival.includes('night'))) &&
            (!filters.type ||
                filters.type.length === 0 ||
                (bus.isVip && filters.type.includes('vip')) ||
                (!bus.isVip && filters.type.includes('normal')))
        );
    }

    override routeExtraInfoTemplate(params: {
        departurePackage: BusPackage;
        returnPackage?: BusPackage;
    }) {
        // in auto-reserve this is undefined
        // need to check more later
        if (!params.departurePackage) {
            return null;
        }
        const duration = secondsToDurationTimeSeparated(
            params.departurePackage.duration
        );

        const returnDuration = params.returnPackage
            ? secondsToDurationTimeSeparated(params.returnPackage.duration)
            : null;
        const departureMinutes = duration.minutes
            ? `و ${duration.minutes} دقیقه`
            : '';

        const returnMinutes = returnDuration?.minutes
            ? `و ${returnDuration.minutes} دقیقه`
            : '';

        return params.returnPackage
            ? `مسافت مسیر رفت ${params.returnPackage.distance} کیلومتر و مسیر برگشت ${params.departurePackage.distance} کیلومتر است. زمان تقریبی سفر بر اساس مسافت و
               بدون درنظر گرفتن توقف های احتمالی، در مسیر رفت ${duration.hours} ساعت ${departureMinutes} و در مسیر برگشت ${returnDuration?.hours ?? 0} ساعت ${returnMinutes} خواهد بود.
               زمان تقریبی ورود به مقصد در مسیر رفت ${params.departurePackage.arrivalTime} و در مسیر برگشت ${params.returnPackage.arrivalTime} خواهد بود.`
            : `مسافت این مسیر ${
                  params.departurePackage.distance || '-'
              } کیلومتر و مدت زمان تقریبی این سفر بدون احتساب توقف‌های احتمالی ${
                  duration.hours
              } ساعت ${departureMinutes} است. (زمان تقریبی ورود به مقصد ${
                  params.departurePackage.arrivalTime
              }).`;
    }

    override get filters(): Filter[] {
        if (!this.currentPackageSet || !this.currentPackageSet.filterMetaData) {
            return [];
        }

        const filterData = this.currentPackageSet.filterMetaData;
        const form = this.searchForm;
        return this.sortFilters([
            new PriceFilter(
                BusFilters.Price,
                filterData.minPrice,
                filterData.maxPrice
            ),
            new TimeFilter(
                BusFilters.Time,
                form.origin?.title ?? '',
                form.destination?.title ?? ''
            ),
            new ButtonGroupFilter(
                BusFilters.BusType,
                [
                    {
                        id: 'normal',
                        title: 'عادی',
                    },
                    {
                        id: 'vip',
                        title: 'VIP',
                    },
                ],
                this.reserveType === 'bus' ? 'نوع اتوبوس' : 'نوع سواری'
            ),
            new CheckboxFilter(
                BusFilters.Carrier,
                filterData.corporations.map(c => ({
                    title: c.name,
                    id: c.id.toString(),
                })),
                'شرکت مسافربری'
            ),
        ]);
    }

    override get metaTagTitle(): string {
        const all = '(همه پایانه‌ها)';
        return `خرید بلیط ${this.reserveInfo.title} ${this.originName.replace(all, '')} ${this.destinationName.replace(all, '')} با بهترین قیمت${this.originName.includes(all) || this.destinationName.includes(all) ? ` ${all}` : ''} - مِستربلیط`;
    }

    override async loadAvailable(params: { index: number }) {
        const promise = super.loadAvailable(params);
        const form = this.searchForm;
        if (this.currentPackageSet) {
            form.setOriginBaseOnCode(
                this.isSelectingReturn
                    ? this.currentPackageSet.destinationTerminalCode
                    : this.currentPackageSet.originTerminalCode
            );
            form.setDestinationBaseOnCode(
                this.isSelectingReturn
                    ? this.currentPackageSet.originTerminalCode
                    : this.currentPackageSet.destinationTerminalCode
            );
        }
        form.loadDestinations();

        await promise;
    }

    override getItineraryPackageLoaderConfigs(
        params: BusUrlParams
    ): BusPackageLoaderConfig[] {
        const form = this.searchForm;
        const ret: BusPackageLoaderConfig[] = [
            {
                originCode: params.origin,
                destinationCode: params.destination,
                originTerminalId: params.originTerminalId,
                destinationTerminalId: params.destinationTerminalId,
                date: params.departureDate,
                totalSeatCount: params.adultCount,
                specificProviderId: form.specificProviderId,
                url: params.url,
                originName: form.origin?.title,
                destinationName: form.destination?.title,
                isRoundTrip: this.isRoundTrip,
            },
        ];

        if (params.returnDate) {
            ret.push({
                originCode: params.destination,
                destinationCode: params.origin,
                originTerminalId: params.destinationTerminalId,
                destinationTerminalId: params.originTerminalId,
                date: params.returnDate,
                totalSeatCount: params.adultCount,
                specificProviderId: form.specificProviderId,
                url: params.url,
                originName: form.destination?.title,
                destinationName: form.origin?.title,
                isRoundTrip: this.isRoundTrip,
            });
        }
        return ret;
    }

    override getInitialFilters(): { [key in BusFiltersData['key']]: any } {
        return {
            price: null,
            carrier: null,
            time: null,
            type: null,
        };
    }

    override async formAutoReserve(query: LocationQuery) {
        const busForm = useBusForm();
        const params = await busForm.parsePath(query as BusPathData);
        const itineraryStore = useItineraryStore();
        const searchFormStore = useSearchFormStore();
        await busForm.loadViewUrlParams(params);
        searchFormStore.urlParams = params;
        itineraryStore.initItineraryModule(params);
        searchFormStore.initSearchForm();
        if (this.currentLoadingStatus !== LoadingStatus.Loading) {
            itineraryStore.setPackageSetIndex(0);
            if (!this.isSetLoaded(0)) {
                await this.loadAvailable({
                    index: 0,
                    // inForm: true,
                });
                if (
                    this.itineraryPackageSets[0]?.loadingStatus ===
                    LoadingStatus.Error
                ) {
                    throw new Error('در حال حاضر سرور اتوبوس قطع می‌باشد.');
                }
            }
        }
        const formStore = useFormStore();
        formStore.setCounts({
            adultCount: params.adultCount,
            childCount: 0,
            infantCount: 0,
        });
        const packageSelectStore = usePackageSelectStore();

        const returnBus = query?.returnBus;
        const departureBus = query?.departureBus;
        const departurePackage = this.sortedReservableList.find(
            ({ uniqueId }) => uniqueId === departureBus
        );

        if (departurePackage) {
            this.beforeSelectPackage({
                index: 0,
                package: departurePackage,
            });
            packageSelectStore.setSelectedPackage({
                index: 0,
                package: departurePackage,
            });
        } else {
            throw new Error('اتوبوس یافت نشد!');
        }
        if (
            params.returnDate &&
            returnBus &&
            this.itineraryPackageSets.length > 1
        ) {
            itineraryStore.setPackageSetIndex(1);
            if (!this.isSetLoaded(1)) {
                await this.loadAvailable({
                    index: 1,
                    // inForm: true,
                });
            }
            if (
                this.itineraryPackageSets[1]?.loadingStatus ===
                LoadingStatus.Error
            ) {
                throw new Error('در حال حاضر سرور اتوبوس قطع می‌باشد.');
            }
            const returnPackage = this.sortedReservableList.find(
                ({ uniqueId }) => uniqueId === returnBus
            );
            if (returnPackage) {
                this.beforeSelectPackage({
                    index: 1,
                    package: returnPackage,
                });
                packageSelectStore.setSelectedPackage({
                    index: 1,
                    package: returnPackage,
                });
            } else {
                throw new Error('اتوبوس برگشت یافت نشد!');
            }
        }
        formStore.clearState();
        formStore.setItineraryPackagesAndDepartureTime(
            packageSelectStore.selectedPackages
        );
    }
}
